// metrc-inventory/product-import.schema.ts
import { z } from "zod";
var ProductMetrcPackageMatchStrategySchema = z.enum([
  "pinned",
  "exact",
  "heuristic",
  "manual",
  "none"
]);
var ProductMetrcPackageReconciliationClassSchema = z.enum([
  "finished",
  "discontinued",
  "absent",
  "item_id_changed"
]);
var ProductMetrcPackageCandidateDetailSchema = z.object({
  metrcItemId: z.number().int().nullable(),
  metrcItemName: z.string(),
  quantity: z.number(),
  unitOfMeasure: z.string(),
  labTestStatus: z.string(),
  packagedDate: z.string(),
  receivedDate: z.string().nullable(),
  productionBatchNumber: z.string().nullable(),
  location: z.string().nullable()
});
var ProductMetrcPackageCandidateSchema = ProductMetrcPackageCandidateDetailSchema.extend({
  packageLabel: z.string().min(1)
});
var ProductMetrcPackageAlreadyImportedRowSchema = z.object({
  inventoryPackageId: z.string().uuid(),
  packageLabel: z.string().min(1),
  localQuantity: z.number(),
  metrcQuantity: z.number(),
  metrcUnitOfMeasure: z.string()
});
var ProductMetrcPackageReconciliationContextSchema = z.object({
  inventoryPackageId: z.string().uuid(),
  packageLabel: z.string().min(1),
  packageQuantity: z.number(),
  finishedDate: z.string().nullable(),
  metrcPackageState: z.string().nullable(),
  packageMetrcItemId: z.number().int().nullable(),
  currentMetrcItemId: z.number().int().nullable(),
  currentMetrcItemName: z.string().nullable()
});
var ProductMetrcPackageReconciliationRowSchema = ProductMetrcPackageReconciliationContextSchema.extend({
  reconciliationClass: ProductMetrcPackageReconciliationClassSchema,
  activeReservationQuantity: z.number()
});
var ProductMetrcVariantCaseConfigSchema = z.object({
  unitWeight: z.number().positive().nullable(),
  unitsPerCase: z.number().int().positive().nullable()
});
var FindProductMetrcPackageCandidatesRequestSchema = z.object({
  facilityLicenseNumber: z.string().min(1),
  stockLocationId: z.string().uuid(),
  manualSearchTerm: z.string().trim().min(1).max(200).optional()
});
var FindProductMetrcPackageCandidatesInputSchema = FindProductMetrcPackageCandidatesRequestSchema.extend({
  organizationId: z.string().uuid(),
  productId: z.string().uuid(),
  memberId: z.string().optional()
});
var FindProductMetrcPackageCandidatesResponseSchema = z.object({
  candidates: z.array(ProductMetrcPackageCandidateSchema),
  matchStrategy: ProductMetrcPackageMatchStrategySchema,
  attemptedTerms: z.array(z.string()),
  hint: z.string().optional(),
  reconciliationRows: z.array(ProductMetrcPackageReconciliationRowSchema),
  alreadyImported: z.array(ProductMetrcPackageAlreadyImportedRowSchema),
  variantId: z.string().uuid(),
  variantCaseConfig: ProductMetrcVariantCaseConfigSchema,
  facilityLicenseNumber: z.string().min(1),
  totalPackagesFetched: z.number().int()
});
var ProductMetrcPackageImportConfigSchema = z.object({
  unitWeight: z.number().positive().optional(),
  unitsPerCase: z.number().int().positive().optional()
});
var ImportProductMetrcPackagesRequestSchema = z.object({
  facilityLicenseNumber: z.string().min(1),
  stockLocationId: z.string().uuid(),
  selectedPackageLabels: z.array(z.string().min(1)).min(1, "At least one package label must be selected"),
  metrcConfig: ProductMetrcPackageImportConfigSchema.optional()
});
var ImportProductMetrcPackagesInputSchema = ImportProductMetrcPackagesRequestSchema.extend(
  {
    organizationId: z.string().uuid(),
    productId: z.string().uuid(),
    portalUserId: z.string().min(1),
    memberId: z.string().optional(),
    filename: z.string().optional()
  }
);
var ImportProductMetrcPackagesResultPackageSchema = z.object({
  packageLabel: z.string().min(1),
  inventoryPackageId: z.string().uuid(),
  quantity: z.number(),
  packagedDate: z.string()
});
var ImportProductMetrcPackagesRejectionSchema = z.object({
  packageLabel: z.string().min(1),
  reason: z.string()
});
var ImportProductMetrcPackagesResultSchema = z.object({
  success: z.boolean(),
  created: z.array(ImportProductMetrcPackagesResultPackageSchema),
  skippedExisting: z.array(ImportProductMetrcPackagesRejectionSchema),
  rejected: z.array(ImportProductMetrcPackagesRejectionSchema),
  totalCasesImported: z.number().int(),
  pinnedMetrcItemId: z.number().int().nullable(),
  pinnedMetrcItemName: z.string().nullable()
});
var ProductMetrcPackageCandidateTransportSchema = ProductMetrcPackageCandidateDetailSchema.extend(
  {
    tag: z.string()
  }
);
var ProductMetrcPackageReconciliationTransportRowSchema = ProductMetrcPackageReconciliationContextSchema.extend({
  driftClass: ProductMetrcPackageReconciliationClassSchema,
  activeReservedQuantity: z.number()
});
var MetrcMatchStrategySchema = ProductMetrcPackageMatchStrategySchema;
var MetrcDriftClassSchema = ProductMetrcPackageReconciliationClassSchema;
var MetrcCandidatePackageSchema = ProductMetrcPackageCandidateTransportSchema;
var MetrcDriftDetectedRowSchema = ProductMetrcPackageReconciliationTransportRowSchema;
var MetrcAlreadyImportedRowSchema = ProductMetrcPackageAlreadyImportedRowSchema;
var MetrcVariantCaseConfigSchema = ProductMetrcVariantCaseConfigSchema;
var MetrcFindCandidatesRequestSchema = z.object({
  licenseNumber: z.string().min(1),
  stockLocationId: z.string().uuid(),
  manualSearchTerm: z.string().trim().min(1).max(200).optional()
});
var MetrcFindCandidatesResponseSchema = z.object({
  candidates: z.array(MetrcCandidatePackageSchema),
  matchStrategy: MetrcMatchStrategySchema,
  attemptedTerms: z.array(z.string()),
  hint: z.string().optional(),
  driftDetected: z.array(MetrcDriftDetectedRowSchema),
  alreadyImported: z.array(MetrcAlreadyImportedRowSchema),
  variantId: z.string().uuid(),
  variantCaseConfig: MetrcVariantCaseConfigSchema,
  licenseNumber: z.string(),
  totalPackagesFetched: z.number().int()
});
var MetrcImportPackagesRequestSchema = z.object({
  licenseNumber: z.string().min(1),
  stockLocationId: z.string().uuid(),
  selectedTags: z.array(z.string().min(1)).min(1, "At least one tag must be selected"),
  metrcConfig: ProductMetrcPackageImportConfigSchema.optional()
});
var MetrcImportResultPackageSchema = z.object({
  tag: z.string(),
  inventoryPackageId: z.string().uuid(),
  quantity: z.number(),
  packagedDate: z.string()
});
var MetrcImportResultRejectionSchema = z.object({
  tag: z.string(),
  reason: z.string()
});
var MetrcImportResultResponseSchema = z.object({
  success: z.boolean(),
  created: z.array(MetrcImportResultPackageSchema),
  skippedExisting: z.array(MetrcImportResultRejectionSchema),
  rejected: z.array(MetrcImportResultRejectionSchema),
  totalCasesImported: z.number().int(),
  pinnedMetrcItemId: z.number().int().nullable(),
  pinnedMetrcItemName: z.string().nullable()
});

export {
  ProductMetrcPackageMatchStrategySchema,
  ProductMetrcPackageReconciliationClassSchema,
  ProductMetrcPackageCandidateSchema,
  ProductMetrcPackageAlreadyImportedRowSchema,
  ProductMetrcPackageReconciliationRowSchema,
  ProductMetrcVariantCaseConfigSchema,
  FindProductMetrcPackageCandidatesRequestSchema,
  FindProductMetrcPackageCandidatesInputSchema,
  FindProductMetrcPackageCandidatesResponseSchema,
  ProductMetrcPackageImportConfigSchema,
  ImportProductMetrcPackagesRequestSchema,
  ImportProductMetrcPackagesInputSchema,
  ImportProductMetrcPackagesResultPackageSchema,
  ImportProductMetrcPackagesRejectionSchema,
  ImportProductMetrcPackagesResultSchema,
  MetrcMatchStrategySchema,
  MetrcDriftClassSchema,
  MetrcCandidatePackageSchema,
  MetrcDriftDetectedRowSchema,
  MetrcAlreadyImportedRowSchema,
  MetrcVariantCaseConfigSchema,
  MetrcFindCandidatesRequestSchema,
  MetrcFindCandidatesResponseSchema,
  MetrcImportPackagesRequestSchema,
  MetrcImportResultPackageSchema,
  MetrcImportResultRejectionSchema,
  MetrcImportResultResponseSchema
};
